import type { UnifiedAccountComponents } from 'unified-auth-core-lib';

export const UNIFIED_ACCOUNT = 'UNIFIED_ACCOUNT';
export const DASHWORKS = 'DASHWORKS';
export const DATASYNC = 'DATASYNC';
export const MCP = 'MCP';
export const MICROSOFT_ADS = 'MICROSOFT_ADS';
export const MICROSOFT_DRIVE = 'MICROSOFT_DRIVE';
export const GOOGLE_SHEETS = 'GOOGLE_SHEETS';
export const ZOOMINFO = 'ZOOMINFO';
export const SLACK_INTEGRATION = 'SLACK_INTEGRATION';
export const SURVEY_MONKEY = 'SURVEY_MONKEY';
export const REDDIT_ADS = 'REDDIT_ADS';
export const CHATGPT_ADS = 'CHATGPT_ADS';
export const ADOBE_SIGN = 'ADOBE_SIGN';
export const CUSTOM_VENDOR_CONNECTION = 'CUSTOM_VENDOR_CONNECTION';
export const GEMINI = 'GEMINI';
export const COHERE = 'COHERE';
export const X_AI = 'X_AI';
export const DEFAULT_PROVIDER_KEY = '__DEFAULT__';

export const PROVIDER_KEYS = {
  DEFAULT: DEFAULT_PROVIDER_KEY,
  UNIFIED_ACCOUNT,
  DASHWORKS,
  DATASYNC,
  MCP,
  MICROSOFT_ADS,
  MICROSOFT_DRIVE,
  GOOGLE_SHEETS,
  ZOOMINFO,
  SLACK_INTEGRATION,
  SURVEY_MONKEY,
  REDDIT_ADS,
  CHATGPT_ADS,
  ADOBE_SIGN,
  CUSTOM_VENDOR_CONNECTION,
  GEMINI,
  COHERE,
  X_AI,
} as const;

export type WellKnownProviderKey =
  (typeof PROVIDER_KEYS)[keyof typeof PROVIDER_KEYS];
export type AuthProviderKey = WellKnownProviderKey | (string & {});

export type SyncProviderRegistration = {
  components: UnifiedAccountComponents;
  appIds?: number[];
  overwrite?: boolean;
};

export type LazyTranslationsSpork = {
  source: string;
};

export type AsyncProviderRegistration = {
  load: () => Promise<UnifiedAccountComponents>;
  appIds: number[];
  lazyLoadedTranslations?: unknown;
};

export type ProviderRegistration =
  | SyncProviderRegistration
  | AsyncProviderRegistration;

export type ProviderLoadType = 'sync' | 'async' | 'none';

export interface AuthRegistry {
  registerProvider(
    providerKey: AuthProviderKey,
    registration: ProviderRegistration
  ): void;

  unregisterProvider(providerKey: AuthProviderKey): boolean;

  getProviderType(
    appId: number,
    providerKey?: AuthProviderKey
  ): ProviderLoadType;

  getComponentsSync(
    appId: number,
    providerKey?: AuthProviderKey
  ): UnifiedAccountComponents | undefined;

  resolveComponents(
    appId: number,
    providerKey?: AuthProviderKey
  ): Promise<UnifiedAccountComponents | undefined>;

  clearRegistry(): void;
}

export interface I18nLoader {
  register: (lang: unknown) => Promise<unknown>;
}

export interface CreateAuthRegistryOptions {
  initializer?: (registry: AuthRegistry) => void;
  i18nLoader?: I18nLoader;
  translationsTimeoutMs?: number;
  onClear?: () => void;
}

export function createAuthRegistry(
  options?: CreateAuthRegistryOptions
): AuthRegistry {
  const componentsByKey = new Map<AuthProviderKey, UnifiedAccountComponents>();
  const providerKeyByAppId = new Map<number, AuthProviderKey>();
  const descriptors = new Map<AuthProviderKey, AsyncProviderRegistration>();
  const descriptorKeyByAppId = new Map<number, AuthProviderKey>();
  const loadingPromises = new Map<
    AuthProviderKey,
    Promise<UnifiedAccountComponents | undefined>
  >();
  const failedLoads = new Set<AuthProviderKey>();
  let initialized = !options?.initializer;
  // eslint-disable-next-line prefer-const -- assigned after function declarations that reference it
  let registry: AuthRegistry;

  function ensureInitialized(): void {
    if (!initialized) {
      initialized = true;
      options!.initializer!(registry);
    }
  }

  function registerProvider(
    providerKey: AuthProviderKey,
    registration: ProviderRegistration
  ): void {
    if ('components' in registration) {
      const { components, appIds = [], overwrite = false } = registration;

      if (componentsByKey.has(providerKey) && !overwrite) {
        console.warn(
          `[unified-auth] Provider "${providerKey}" already registered. Use overwrite: true to replace.`
        );
        return;
      }

      componentsByKey.set(providerKey, components);

      for (const appId of appIds) {
        providerKeyByAppId.set(Number(appId), providerKey);
      }
    } else {
      descriptors.set(providerKey, registration);

      for (const appId of registration.appIds) {
        descriptorKeyByAppId.set(Number(appId), providerKey);
      }
    }
  }

  function unregisterProvider(providerKey: AuthProviderKey): boolean {
    const existed = componentsByKey.delete(providerKey);
    descriptors.delete(providerKey);

    for (const [appId, key] of providerKeyByAppId) {
      if (key === providerKey) {
        providerKeyByAppId.delete(appId);
      }
    }

    for (const [appId, key] of descriptorKeyByAppId) {
      if (key === providerKey) {
        descriptorKeyByAppId.delete(appId);
      }
    }

    return existed;
  }

  function getProviderType(
    appId: number,
    providerKey?: AuthProviderKey
  ): ProviderLoadType {
    ensureInitialized();

    if (providerKey) {
      if (componentsByKey.has(providerKey)) return 'sync';
      if (descriptors.has(providerKey)) return 'async';
    }

    const id = Number(appId);
    if (providerKeyByAppId.has(id)) return 'sync';
    if (descriptorKeyByAppId.has(id)) return 'async';
    if (componentsByKey.has(DEFAULT_PROVIDER_KEY)) return 'sync';

    return 'none';
  }

  function getComponentsSync(
    appId: number,
    providerKey?: AuthProviderKey
  ): UnifiedAccountComponents | undefined {
    ensureInitialized();

    if (providerKey) {
      return componentsByKey.get(providerKey);
    }

    const key = providerKeyByAppId.get(Number(appId));
    if (key) {
      return componentsByKey.get(key);
    }

    return componentsByKey.get(DEFAULT_PROVIDER_KEY);
  }

  function resolveComponents(
    appId: number,
    providerKey?: AuthProviderKey
  ): Promise<UnifiedAccountComponents | undefined> {
    ensureInitialized();

    const id = Number(appId);
    const syncResult = providerKey
      ? componentsByKey.get(providerKey)
      : (() => {
          const key = providerKeyByAppId.get(id);
          return key ? componentsByKey.get(key) : undefined;
        })();

    if (syncResult) {
      return Promise.resolve(syncResult);
    }

    const resolverKey = providerKey ?? descriptorKeyByAppId.get(id);

    if (!resolverKey) {
      return Promise.resolve(componentsByKey.get(DEFAULT_PROVIDER_KEY));
    }

    if (componentsByKey.has(resolverKey)) {
      return Promise.resolve(componentsByKey.get(resolverKey));
    }

    const descriptor = descriptors.get(resolverKey);

    if (!descriptor) {
      return Promise.resolve(componentsByKey.get(DEFAULT_PROVIDER_KEY));
    }

    if (failedLoads.has(resolverKey)) {
      return Promise.resolve(componentsByKey.get(DEFAULT_PROVIDER_KEY));
    }

    if (!loadingPromises.has(resolverKey)) {
      const i18nLoader = options?.i18nLoader;

      const timeoutMs = options?.translationsTimeoutMs ?? 30000;

      let timeoutHandle: ReturnType<typeof setTimeout>;
      const translationsPromise =
        i18nLoader && descriptor.lazyLoadedTranslations
          ? Promise.race([
              Promise.resolve()
                .then(() =>
                  i18nLoader.register(descriptor.lazyLoadedTranslations)
                )
                .then(
                  (result) => {
                    clearTimeout(timeoutHandle);
                    return result;
                  },
                  (translationError: unknown) => {
                    clearTimeout(timeoutHandle);
                    console.warn(
                      `[unified-auth] Failed to load translations for "${resolverKey}":`,
                      translationError
                    );
                  }
                ),
              new Promise<void>((resolve) => {
                timeoutHandle = setTimeout(() => {
                  console.warn(
                    `[unified-auth] Translation loading timed out for "${resolverKey}", rendering without translations`
                  );
                  resolve();
                }, timeoutMs);
              }),
            ])
          : Promise.resolve();

      const promise = Promise.all([descriptor.load(), translationsPromise])
        .then(([components]) => {
          componentsByKey.set(resolverKey, components);

          for (const descriptorAppId of descriptor.appIds) {
            providerKeyByAppId.set(Number(descriptorAppId), resolverKey);
          }

          loadingPromises.delete(resolverKey);
          return components;
        })
        .catch((error) => {
          clearTimeout(timeoutHandle);
          loadingPromises.delete(resolverKey);
          failedLoads.add(resolverKey);
          console.error(
            `[unified-auth] Failed to load provider "${resolverKey}":`,
            error
          );
          return componentsByKey.get(DEFAULT_PROVIDER_KEY);
        });

      loadingPromises.set(resolverKey, promise);
    }

    return loadingPromises.get(resolverKey)!;
  }

  function clearRegistry(): void {
    componentsByKey.clear();
    providerKeyByAppId.clear();
    descriptors.clear();
    descriptorKeyByAppId.clear();
    loadingPromises.clear();
    failedLoads.clear();
    initialized = !options?.initializer;
    options?.onClear?.();
  }

  registry = {
    registerProvider,
    unregisterProvider,
    getProviderType,
    getComponentsSync,
    resolveComponents,
    clearRegistry,
  };

  return registry;
}
